import { useState } from 'react';
import { securityApi } from '../../api/services';
import type { PermissionDto, RoleDto, SaveRoleDto, SecurityUserDto } from '../../api/types';
import {
  Alert, Badge, Button, Check, Field, Form, Input, Loadable, Modal, PageHeader, Select, Table, Tabs, useAction, useAsync,
} from '../../ui/components';
import { PageHelp } from '../../ui/help';

type TabKey = 'users' | 'roles';

/** Usuarios y roles de la contabilidad: qué puede hacer cada usuario del comercio. */
export function SecurityPage() {
  const [tab, setTab] = useState<TabKey>('users');
  const status = useAsync(() => securityApi.status(), []);
  const roles = useAsync(() => securityApi.roles(), []);
  const users = useAsync(() => securityApi.users(), []);
  const permissions = useAsync(() => securityApi.permissions(), []);
  const reloadAll = () => { status.reload(); roles.reload(); users.reload(); };

  return (
    <>
      <PageHeader title="Usuarios y roles" subtitle="Quién puede registrar, aplicar, anular, cerrar o sólo consultar" />
      <PageHelp page="security" />
      <Loadable state={status}>
        {(s) => !s.available
          ? <Alert kind="warn">Esta función todavía no está habilitada en la base de datos de su empresa. Contacte al administrador del sistema.</Alert>
          : s.rolesActive
            ? <Alert kind="ok">Los roles están activos: {s.usersWithRoles} usuario(s) con rol. Un usuario sin rol no puede entrar a la contabilidad.</Alert>
            : <Alert kind="warn">Todavía no hay roles asignados: <strong>todos los usuarios del comercio tienen acceso completo</strong>. Cree los roles, asígnese primero el rol Administrador y después asigne roles a los demás.</Alert>}
      </Loadable>
      <Tabs<TabKey> tabs={[{ key: 'users', label: 'Usuarios' }, { key: 'roles', label: 'Roles' }]} active={tab} onChange={setTab} />
      {tab === 'users' && (
        <Loadable state={users}>
          {(u) => <UsersTab users={u} roles={roles.data ?? []} onChanged={reloadAll} goRoles={() => setTab('roles')} />}
        </Loadable>
      )}
      {tab === 'roles' && (
        <Loadable state={roles}>
          {(r) => <RolesTab roles={r} permissions={permissions.data ?? []} onChanged={reloadAll} />}
        </Loadable>
      )}
    </>
  );
}

function UsersTab({ users, roles, onChanged, goRoles }: { users: SecurityUserDto[]; roles: RoleDto[]; onChanged: () => void; goRoles: () => void }) {
  const [editing, setEditing] = useState<SecurityUserDto | null>(null);
  const name = (id: number) => roles.find((r) => r.id === id);
  return (
    <>
      {roles.length === 0 && <Alert kind="info">Todavía no hay roles. <Button kind="link" onClick={goRoles}>Créelos en la pestaña Roles</Button> (puede empezar con los roles sugeridos).</Alert>}
      <Table rows={users} rowKey={(u) => u.id} empty="La empresa no tiene usuarios registrados." columns={[
        { key: 'u', title: 'Usuario', render: (u) => <>{u.user}{u.isCurrentUser && <> <Badge>usted</Badge></>}</> },
        { key: 'n', title: 'Nombre', render: (u) => u.name ?? '' },
        { key: 'r', title: 'Roles', render: (u) => u.roleIds.length === 0
          ? <span className="acc-muted">Sin rol</span>
          : u.roleIds.map((id) => { const r = name(id); return <Badge key={id} tone={r?.status === 'A' ? 'ok' : 'warn'}>{r?.name ?? `#${id}`}{r?.status === 'I' ? ' (inactivo)' : ''}</Badge>; }) },
        { key: 'x', title: '', render: (u) => (roles.length > 0 ? <Button kind="link" onClick={() => setEditing(u)}>Cambiar roles…</Button> : null) },
      ]} />
      {editing && <UserRolesModal user={editing} roles={roles} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </>
  );
}

function UserRolesModal({ user, roles, onClose, onSaved }: { user: SecurityUserDto; roles: RoleDto[]; onClose: () => void; onSaved: () => void }) {
  const [selected, setSelected] = useState<number[]>(user.roleIds);
  const { run, busy } = useAction();
  const toggle = (id: number, on: boolean) => setSelected((s) => (on ? [...s, id] : s.filter((x) => x !== id)));
  const removingAll = selected.length === 0 && user.roleIds.length > 0;
  return (
    <Modal title={`Roles de ${user.user}`} onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { await securityApi.setUserRoles(user.id, selected); onSaved(); },
        removingAll ? { confirm: 'El usuario quedará sin roles y no podrá entrar a la contabilidad. ¿Continuar?', danger: true, success: 'Roles actualizados.' } : { success: 'Roles actualizados.' })}>
        {roles.map((r) => (
          <div key={r.id} style={{ marginBottom: 6 }}>
            <Check checked={selected.includes(r.id)} onChange={(v) => toggle(r.id, v)} label={`${r.name}${r.status === 'I' ? ' (inactivo)' : ''}`} />
            {r.description && <div className="acc-muted" style={{ marginLeft: 24, fontSize: '0.9em' }}>{r.description}</div>}
          </div>
        ))}
        <Alert kind="info">Si el usuario tiene varios roles, puede hacer todo lo que permita cualquiera de ellos.</Alert>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}

function RolesTab({ roles, permissions, onChanged }: { roles: RoleDto[]; permissions: PermissionDto[]; onChanged: () => void }) {
  const [editing, setEditing] = useState<RoleDto | 'new' | null>(null);
  const { run, busy } = useAction();
  const label = (code: string) => permissions.find((p) => p.code === code)?.name ?? code;
  return (
    <>
      <div className="acc-actions" style={{ justifyContent: 'flex-start' }}>
        <Button kind="primary" onClick={() => setEditing('new')}>Nuevo rol</Button>
        <Button busy={busy} onClick={() => run(async () => { await securityApi.createSuggested(); onChanged(); }, { success: 'Roles sugeridos listos.' })}>Crear roles sugeridos</Button>
        <span className="acc-muted">Administrador, Contador, Auxiliar contable y Consulta. Sólo se crean los que no existan; después puede ajustarlos.</span>
      </div>
      <Table rows={roles} rowKey={(r) => r.id} empty="No hay roles." columns={[
        { key: 'n', title: 'Rol', render: (r) => <><strong>{r.name}</strong>{r.description && <div className="acc-muted">{r.description}</div>}</> },
        { key: 'e', title: 'Estado', render: (r) => <Badge tone={r.status === 'A' ? 'ok' : 'neutral'}>{r.status === 'A' ? 'Activo' : 'Inactivo'}</Badge> },
        { key: 'p', title: 'Permisos', render: (r) => (r.permissions.length === permissions.length && permissions.length > 0 ? 'Todos' : r.permissions.map(label).join(', ')) },
        { key: 'u', title: 'Usuarios', align: 'right', render: (r) => r.users },
        { key: 'x', title: '', render: (r) => (
          <>
            <Button kind="link" onClick={() => setEditing(r)}>Editar…</Button>
            {r.users === 0 && <Button kind="link" busy={busy} onClick={() => run(async () => { await securityApi.deleteRole(r.id); onChanged(); }, { confirm: `¿Eliminar el rol "${r.name}"?`, danger: true, success: 'Rol eliminado.' })}>Eliminar</Button>}
          </>
        ) },
      ]} />
      {editing && <RoleModal role={editing === 'new' ? null : editing} permissions={permissions} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </>
  );
}

function RoleModal({ role, permissions, onClose, onSaved }: { role: RoleDto | null; permissions: PermissionDto[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<SaveRoleDto>({ name: role?.name ?? '', description: role?.description ?? '', status: role?.status ?? 'A', permissions: role?.permissions ?? [] });
  const { run, busy, fieldErrors } = useAction();
  const groups = Array.from(new Set(permissions.map((p) => p.group)));
  const toggle = (code: string, on: boolean) => setForm((f) => ({ ...f, permissions: on ? [...f.permissions, code] : f.permissions.filter((x) => x !== code) }));
  const toggleGroup = (group: string, on: boolean) => {
    const codes = permissions.filter((p) => p.group === group).map((p) => p.code);
    setForm((f) => ({ ...f, permissions: on ? Array.from(new Set([...f.permissions, ...codes])) : f.permissions.filter((x) => !codes.includes(x)) }));
  };
  return (
    <Modal title={role ? `Editar rol ${role.name}` : 'Nuevo rol'} onClose={onClose} size="lg">
      <Form onSubmit={() => run(async () => {
        const dto = { ...form, name: form.name.trim(), description: form.description?.trim() || null };
        if (role) await securityApi.updateRole(role.id, dto); else await securityApi.createRole(dto);
        onSaved();
      }, { success: role ? 'Rol actualizado.' : 'Rol creado.' })}>
        <div className="acc-grid-form">
          <Field label="Nombre" error={fieldErrors.Name}><Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={60} required /></Field>
          <Field label="Estado"><Select value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={[{ value: 'A', label: 'Activo' }, { value: 'I', label: 'Inactivo (sus usuarios pierden estos permisos)' }]} /></Field>
        </div>
        <Field label="Descripción" wide error={fieldErrors.Description}><Input value={form.description ?? ''} onChange={(v) => setForm({ ...form, description: v })} maxLength={200} /></Field>
        <h4>Permisos</h4>
        {groups.map((g) => {
          const items = permissions.filter((p) => p.group === g);
          const all = items.every((p) => form.permissions.includes(p.code));
          return (
            <fieldset key={g} className="acc-fieldset" style={{ border: '1px solid var(--acc-border)', borderRadius: 6, margin: '8px 0', padding: '6px 10px' }}>
              <legend><Check checked={all} onChange={(v) => toggleGroup(g, v)} label={g} /></legend>
              {items.map((p) => (
                <div key={p.code} style={{ marginBottom: 4 }}>
                  <Check checked={form.permissions.includes(p.code)} onChange={(v) => toggle(p.code, v)} label={p.name} />
                  <div className="acc-muted" style={{ marginLeft: 24, fontSize: '0.9em' }}>{p.description}</div>
                </div>
              ))}
            </fieldset>
          );
        })}
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}
